import { useMemo } from 'react'

import { useCubeReportPageFiltersContext } from 'cube'
import {
  CONVERSATION_FACTS_DIMENSIONS,
  CubeSchemaTables,
} from 'cube/constants/QueryConstants'
import { RoutedChatsTeammatesBaseQuery } from 'cube/queries'

import {
  CubeQueryBuilder,
  resultSetAsTableData,
  useCubeQuery,
} from '@driftt/dds.reports'

import useTimePeriod from 'hooks/useTimePeriod'

import { TeammatesTableMetric, TeammatesTableRow } from './teammates.types'

export interface TeammatesResult {
  tableData: TeammatesTableRow[]
  isLoadingTableData: boolean
  isErrorFetchingTableData: boolean
}

/***
 * Hook that uses cube to fetch teammates routing data
 *
 * @returns {TeammatesResult}
 */
export const useRoutedChatTeammates = () => {
  const timePeriod = useTimePeriod()
  const { pageFilters } = useCubeReportPageFiltersContext()

  const routedTeammatesCubeQuery = new CubeQueryBuilder({
    query: RoutedChatsTeammatesBaseQuery,
  })
    .withFilters([...pageFilters])
    .withinDateRange(
      CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
      timePeriod.startDate.toDate(),
      timePeriod.endDate.toDate()
    )
    .build()

  const {
    data,
    isLoading: isLoadingTableData,
    isError: isErrorFetchingTableData,
  } = useCubeQuery({
    query: routedTeammatesCubeQuery,
  })

  const tableData: TeammatesTableRow[] = useMemo(() => {
    const tableRows = resultSetAsTableData(data) as TeammatesTableMetric[]

    return tableRows.map((row) => {
      return {
        ...row[CubeSchemaTables.CONVERSATIONS],
        ...row[CubeSchemaTables.CONVERSATION_ROUTING],
        ...row[CubeSchemaTables.APP_USERS_ONLY_FIRST_ROUTED],
        ratingAverage:
          row[CubeSchemaTables.DRIFT_CHAT_CONVERSATION_RATINGS].average,
        ratingCount:
          row[CubeSchemaTables.DRIFT_CHAT_CONVERSATION_RATINGS].count,
      }
    })
  }, [data])

  return useMemo(
    () => ({ tableData, isLoadingTableData, isErrorFetchingTableData }),
    [tableData, isLoadingTableData, isErrorFetchingTableData]
  )
}

export default useRoutedChatTeammates
